// src/components/reports/ReportDetailContent.js - Shared full report detail view (admin + journalist)
import React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import FriendlyAnalysis from '../common/FriendlyAnalysis';

const VIOLATION_TYPE_KEYS = ['targeting_civilians', 'forced_displacement', 'property_destruction', 'deprivation_housing_aid', 'arbitrary_arrest', 'hate_speech_incitement', 'other'];

function FieldRow({ label, value, className = '' }) {
  if (value == null || value === '') return null;
  return (
    <div className={className}>
      <label className="text-xs font-medium text-gray-500 dark:text-shayek-dark-500">{label}</label>
      <p className="mt-0.5 text-sm text-gray-900 dark:text-shayek-dark-900 break-words">{String(value)}</p>
    </div>
  );
}

function parseEvidenceLinks(str) {
  if (!str || typeof str !== 'string') return [];
  const parts = str.split(/[\n,\s]+/).map(s => s.trim()).filter(Boolean);
  return parts.filter(s => /^https?:\/\//i.test(s));
}

function getCategoryLabel(category, t) {
  const raw = String(category || '').trim();
  if (!raw) return 'N/A';
  return t(`report.categories.${raw}`, { defaultValue: raw });
}

export default function ReportDetailContent({ report, showStatusBadge = true }) {
  const { t } = useTranslation();
  if (!report) return null;

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400',
      verified: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400',
      rejected: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400',
      flagged: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-400',
      pending_review: 'bg-shayek-primary/10 dark:bg-shayek-primary/20 text-shayek-primary dark:text-shayek-red-light',
    };
    return colors[status] || colors.pending;
  };

  const fd = report.form_data && typeof report.form_data === 'object' ? report.form_data : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {showStatusBadge && (
          <div>
            <label className="text-sm font-medium text-gray-600 dark:text-shayek-dark-600">{t('journalist.reports.details.status')}</label>
            <p className={`mt-1 px-3 py-1 rounded-full text-sm font-medium inline-block ${getStatusColor(report.status)}`}>
              {t(`journalist.reports.status.${report.status}`) || report.status}
            </p>
          </div>
        )}
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-shayek-dark-600">{t('journalist.reports.details.category')}</label>
          <p className="mt-1 text-gray-900 dark:text-shayek-dark-900">{getCategoryLabel(report.category, t)}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-shayek-dark-600">{t('journalist.reports.details.type')}</label>
          <p className="mt-1 text-gray-900 dark:text-shayek-dark-900 capitalize">{report.input_type || '—'}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-shayek-dark-600">{t('journalist.reports.details.date')}</label>
          <p className="mt-1 text-gray-900 dark:text-shayek-dark-900">{report.created_at ? format(new Date(report.created_at), 'MMM d, yyyy HH:mm') : '—'}</p>
        </div>
        {report.updated_at && (
          <div>
            <label className="text-sm font-medium text-gray-600 dark:text-shayek-dark-600">{t('common.updated') || 'Updated'}</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-shayek-dark-900">{format(new Date(report.updated_at), 'MMM d, yyyy HH:mm')}</p>
          </div>
        )}
        <div className="sm:col-span-2">
          <label className="text-sm font-medium text-gray-600 dark:text-shayek-dark-600">{t('journalist.reports.details.id') || 'ID'}</label>
          <p className="mt-1 text-xs text-gray-700 dark:text-shayek-dark-300 font-mono break-all">{report.id}</p>
        </div>
      </div>

      {report.input_preview && (
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-shayek-dark-600">{t('journalist.reports.details.preview') || 'Preview'}</label>
          <p className="mt-1 text-sm text-gray-900 dark:text-shayek-dark-900 break-words">{report.input_preview}</p>
        </div>
      )}

      {report.input_data && (
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-shayek-dark-600">{t('journalist.reports.details.content')}</label>
          <div className="mt-1 p-4 bg-gray-50 dark:bg-shayek-dark-50 rounded-lg border border-gray-200 dark:border-shayek-dark-200">
            <p className="text-gray-900 dark:text-shayek-dark-900 whitespace-pre-wrap">{report.input_data}</p>
          </div>
        </div>
      )}

      {fd && (
        <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-shayek-dark-200">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-shayek-dark-800">{t('report.sections.caseInfo')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fd.case_number && <FieldRow label={t('report.caseNumber')} value={fd.case_number} />}
            {fd.monitoring_date && <FieldRow label={t('report.monitoringDate')} value={fd.monitoring_date} />}
            {fd.reporter_name && <FieldRow label={t('report.reporterName')} value={fd.reporter_name} />}
          </div>
          {fd.violation_type && fd.violation_type.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-shayek-dark-800 mt-4">{t('report.sections.violation')}</h3>
              <div className="flex flex-wrap gap-2">
                {fd.violation_type.map((v, i) => (
                  <span key={i} className="px-2 py-1 bg-shayek-primary/10 dark:bg-shayek-primary/20 text-shayek-primary dark:text-shayek-red-light rounded text-sm">
                    {typeof v === 'string' && VIOLATION_TYPE_KEYS.includes(v) ? t(`report.violationTypeOptions.${v}`) : v}
                  </span>
                ))}
              </div>
            </>
          )}
          <h3 className="text-sm font-semibold text-gray-800 dark:text-shayek-dark-800 mt-4">{t('report.sections.incident')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fd.incident_date && <FieldRow label={t('report.incidentDate')} value={fd.incident_date} />}
            {fd.incident_time && <FieldRow label={t('report.incidentTime')} value={fd.incident_time} />}
            {fd.location_place && <FieldRow label={t('report.locationPlace')} value={fd.location_place} />}
          </div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-shayek-dark-800 mt-4">{t('report.sections.affected')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fd.affected_type && fd.affected_type.length > 0 && (
              <FieldRow label={t('report.affectedType')} value={fd.affected_type.map(x => t(`report.affectedTypeOptions.${x}`) || x).join(', ')} />
            )}
            {fd.affected_category && fd.affected_category.length > 0 && (
              <FieldRow label={t('report.affectedCategory')} value={fd.affected_category.map(x => t(`report.affectedCategoryOptions.${x}`) || x).join(', ')} />
            )}
            {fd.affected_count != null && <FieldRow label={t('report.affectedCount')} value={String(fd.affected_count)} />}
            {fd.affected_count_known && <FieldRow label={t('report.affectedCountKnown')} value={t(`report.affectedCountKnownOptions.${fd.affected_count_known}`) || fd.affected_count_known} />}
          </div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-shayek-dark-800 mt-4">{t('report.sections.responsibility')}</h3>
          <div className="grid grid-cols-1 gap-3">
            {fd.responsible_party && <FieldRow label={t('report.responsibleParty')} value={fd.responsible_party} />}
            {fd.responsible_party_notes && <FieldRow label={t('report.responsiblePartyNotes')} value={fd.responsible_party_notes} />}
          </div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-shayek-dark-800 mt-4">{t('report.sections.sourcesEvidence')}</h3>
          <div className="grid grid-cols-1 gap-3">
            {fd.info_sources && fd.info_sources.length > 0 && (
              <FieldRow label={t('report.infoSources')} value={fd.info_sources.map(x => t(`report.infoSourcesOptions.${x}`) || x).join(', ')} />
            )}
            {(() => {
              const el = fd.evidence_links;
              if (Array.isArray(el) && el.length > 0) {
                return (
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-shayek-dark-500">{t('report.evidenceLinks')}</label>
                    <ul className="mt-1 space-y-1.5">
                      {el.map((item, idx) => {
                        const url = typeof item?.url === 'string' ? item.url : '';
                        if (!url) return null;
                        const label = typeof item?.label === 'string' && item.label.trim() ? item.label.trim() : null;
                        return (
                          <li key={idx}>
                            {label && <span className="text-sm text-gray-700 dark:text-shayek-dark-300">{label}: </span>}
                            <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-shayek-primary dark:text-shayek-red-light hover:underline break-all">
                              {url.length > 60 ? url.slice(0, 57) + '...' : url}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              }
              const urls = parseEvidenceLinks(el);
              if (urls.length === 0) return null;
              return (
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-shayek-dark-500">{t('report.evidenceLinks')}</label>
                  <ul className="mt-1 space-y-1.5">
                    {urls.map((url, idx) => (
                      <li key={idx}>
                        <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-shayek-primary dark:text-shayek-red-light hover:underline break-all">
                          {url.length > 60 ? url.slice(0, 57) + '...' : url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            {(() => {
              const urls = Array.isArray(report.attachment_urls) && report.attachment_urls.length > 0
                ? report.attachment_urls
                : report.attachment_url ? [report.attachment_url] : [];
              if (urls.length === 0) return null;
              const handleDownloadAll = () => {
                urls.forEach((url, i) => {
                  setTimeout(() => {
                    const a = document.createElement('a');
                    a.href = url;
                    a.target = '_blank';
                    a.rel = 'noopener noreferrer';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }, i * 300);
                });
              };
              return (
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-shayek-dark-500">{t('report.attachment')}</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {urls.length >= 2 && (
                      <button
                        type="button"
                        onClick={handleDownloadAll}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-shayek-primary dark:bg-shayek-red-light text-white rounded-lg text-sm font-medium hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-dark transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        {t('report.downloadAllAttachments', 'Download all attachments')}
                      </button>
                    )}
                    {urls.map((attUrl, idx) => (
                      <a key={idx} href={attUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-shayek-primary/10 dark:bg-shayek-primary/20 text-shayek-primary dark:text-shayek-red-light rounded-lg text-sm font-medium hover:bg-shayek-primary/20 dark:hover:bg-shayek-primary/30 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        {urls.length === 1 ? t('report.downloadAttachment', 'Download attachment') : `${t('report.downloadAttachment', 'Download attachment')} ${idx + 1}`}
                      </a>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-shayek-dark-800 mt-4">{t('report.sections.verification')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fd.initial_verification && <FieldRow label={t('report.initialVerification')} value={t(`report.initialVerificationOptions.${fd.initial_verification}`) || fd.initial_verification} />}
            {fd.reliability_level && <FieldRow label={t('report.reliabilityLevel')} value={t(`report.reliabilityLevelOptions.${fd.reliability_level}`) || fd.reliability_level} />}
            {fd.verification_notes && <FieldRow label={t('report.verificationNotes')} value={fd.verification_notes} className="sm:col-span-2" />}
          </div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-shayek-dark-800 mt-4">{t('report.sections.safetyLegal')}</h3>
          <div className="grid grid-cols-1 gap-3">
            {fd.safety_risks && fd.safety_risks.length > 0 && (
              <FieldRow label={t('report.safetyRisks')} value={fd.safety_risks.map(x => t(`report.safetyRisksOptions.${x}`) || x).join(', ')} />
            )}
            {fd.legal_classification && fd.legal_classification.length > 0 && (
              <FieldRow label={t('report.legalClassification')} value={fd.legal_classification.map(x => t(`report.legalClassificationOptions.${x}`) || x).join(', ')} />
            )}
          </div>
          {fd.reporter_notes && (
            <>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-shayek-dark-800 mt-4">{t('report.sections.monitorNotes')}</h3>
              <div className="p-3 bg-gray-50 dark:bg-shayek-dark-50 rounded-lg border border-gray-200 dark:border-shayek-dark-200">
                <p className="text-gray-900 dark:text-shayek-dark-900 whitespace-pre-wrap">{fd.reporter_notes}</p>
              </div>
            </>
          )}
        </div>
      )}

      {report.result_json && (
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-shayek-dark-600">{t('journalist.reports.details.aiAnalysis')}</label>
          <div className="mt-1 p-4 bg-gray-50 dark:bg-shayek-dark-50 rounded-lg border border-gray-200 dark:border-shayek-dark-200">
            <FriendlyAnalysis data={report.result_json} />
          </div>
        </div>
      )}

      {(report.fact_check_result || report.result_json?.fact_check) && (
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-shayek-dark-600">{t('journalist.reports.details.factCheck')}</label>
          <div className="mt-1 p-4 bg-gray-50 dark:bg-shayek-dark-50 rounded-lg border border-gray-200 dark:border-shayek-dark-200">
            <FriendlyAnalysis data={report.fact_check_result || report.result_json?.fact_check} type="fact_check" />
          </div>
        </div>
      )}
    </div>
  );
}
